import { Dictionary, keysFrom, TrackingScopes } from '@moda/portal-astrarium';
import { FeatureFlag } from '@moda/portal-stanchions';
import { always, head, ifElse, isNil, pipe, pluck, reject, uniq } from 'ramda';
import { Cart } from '../hooks/useCart';
import { UseUserQuery_user as User } from '../generated/types/UseUserQuery';
// pretty much every property that Segment asks for is in snake_case.

/**
findVariantInventoryType: This function is here to help with GTM tags.
It returns "Boutique" if the product in scope is a boutique product, or "Trunkshow" otherwise.
These values are intentionally Title Case.
*/
export const findVariantInventoryType = (scope: TrackingScopes) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isBoutique: boolean | undefined = (scope?.variant as Record<string, any> | undefined)
    ?.isBoutique;
  if (isBoutique === undefined) return undefined;
  return isBoutique ? 'Boutique' : 'Trunkshow';
};

/**
findCartItemInventoryType: This function is here to help with GTM tags.
It returns "boutique" if the product in scope is a boutique product, or "trunkshow" otherwise.
These values are intentionally lowercase.
*/
const findCartItemInventoryType = (scope: TrackingScopes) => {
  // `scope` here is the secondary scope, as surfaced in the `properties` of a ListLocator.
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const isTrunkshow = (scope?.attributes as Record<string, any> | undefined)?.isTrunkshow;
  if (isTrunkshow === undefined) return undefined;
  return isTrunkshow ? 'trunkshow' : 'boutique';
};

/**
getBrandForBrandedPLP: This function is here to help with GTM tags.
If the PLP is all from one designer, return that designer as the brand name.
If the PLP is _not_ all from one designer, then set the brand to "" (empty string).
Otherwise, return undefined - thereby not setting the brand.
*/
const getBrandForBrandedPLP = (scope: TrackingScopes) => {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const products = scope?.products as Record<string, any>[] | undefined;
  if (products === undefined) return undefined;
  const brand: string | undefined = pipe(
    pluck('designerName'),
    uniq,
    reject(isNil),
    ifElse(brands => brands.length > 1, always(''), head)
  )(products);
  return brand;
};

const definitions = {
  product_id: ['variant.productId', 'lineItem.attributes.productId'],
  variant: ['variant.id', 'lineItem.attributes.variantId'],
  sku: ['size.sku', 'lineItem.attributes.skuId'],
  name: ['variant.name', 'lineItem.attributes.productName'],
  brand: ['variant.designerName', 'lineItem.attributes.designerName', getBrandForBrandedPLP],
  price: [
    'activeVariant.prices.currentPrice.price',
    'lineItem.attributes.prices.currentPrice.price'
  ],
  original_price: [
    'activeVariant.prices.originalPrice.price',
    'lineItem.attributes.prices.originalPrice.price'
  ],
  image_url: [
    'activeVariant.primaryImage.medium',
    'activeVariant.primaryImageUrls.src',
    'lineItem.attributes.imageSrc'
  ],
  product_availability: ['activeVariant.availability'],
  currency: [
    'activeVariant.prices.currentPrice.currency',
    'lineItem.attributes.prices.currentPrice.currency',
    'order.attributes.currencyUnit',
    'cart.attributes.currency',
    'order.attributes.currency'
  ],
  inventory_type: [findVariantInventoryType],
  products: [
    {
      listAt: ['products'],
      properties: {
        product_id: ['productId', 'attributes.productId'],
        variant: ['id', 'attributes.variantId'],
        name: ['name', 'attributes.productName'],
        brand: ['designerName', 'attributes.designerName'],
        price: ['masterVariant.prices.currentPrice.price', 'attributes.prices.currentPrice.price'],
        original_price: [
          'masterVariant.prices.originalPrice.price',
          'attributes.prices.originalPrice.price'
        ],
        image_url: ['masterVariant.primaryImageUrls.src', 'attributes.imageSrc'],
        product_availability: ['availability'],
        currency: [
          'masterVariant.prices.currentPrice.currency',
          'attributes.prices.currentPrice.currency'
        ],
        inventory_type: [findCartItemInventoryType],
        quantity: ['attributes.quantity'],
        sku: ['attributes.skuId']
      }
    }
  ],
  cart_id: ['cart.id'],
  cart_type: ['cart.attributes.version'],
  order_id: ['order.id'],
  order_number: ['order.attributes.orderNumber'],
  revenue: [
    ({ cart }: TrackingScopes) =>
      Number((cart as Cart | undefined)?.attributes.paymentSummary.total.price || 0) -
      Number((cart as Cart | undefined)?.attributes.paymentSummary.totalShipping.price || 0) -
      Number((cart as Cart | undefined)?.attributes.paymentSummary.totalTaxes.price || 0)
  ],
  total: [
    'order.attributes.paymentSummary.total.price',
    'cart.attributes.paymentSummary.total.price'
  ],
  paid_remaining_balance: ['order.attributes.paymentSummary.orderBalance'],
  shipping: [
    'order.attributes.paymentSummary.shippingAndPackaging.price',
    'cart.attributes.paymentSummary.esimatedShipping.price'
  ],
  tax: [
    'order.attributes.paymentSummary.salesTaxesAndDuties.price',
    'cart.attributes.paymentSummary.totalTaxes.price'
  ],
  subtotal: [
    'cart.attributes.paymentSummary.subtotal.price',
    'order.attributes.totals.orderSubtotal'
  ],
  coupon: ['cart.attributes.giftCode', 'order.attributes.paymentSummary.giftCode'],
  discount: ['order.attributes.paymentSummary.discounts.price'],
  checkout_step: ['checkoutStep'],
  shipping_method: [
    ({ cart }: TrackingScopes) =>
      (cart as Cart | undefined)?.attributes.shippingMethods?.find(
        ({ method }) => method === (cart as Cart | undefined)?.attributes.selectedShippingMethodId
      )?.displayName
  ],
  payment_method: ['cart.attributes.paymentSource'],
  first_order: ['order.attributes.firstOrder'],
  query: ['query'],
  search_type: ['searchType'],
  cj_aid: ['referralParameters.aid'],
  cj_pid: ['referralParameters.pid'],
  cj_sid: ['referralParameters.sid'],
  cj_event: ['referralParameters.cjevent'],
  experiment_id: ['featureFlag.flagName'],
  variation_id: [
    ({ featureFlag }: TrackingScopes) =>
      JSON.stringify((featureFlag as { treatment: FeatureFlag })?.treatment)
  ],
  badge: ['badge'],
  signed_in: [
    ({ user }: TrackingScopes) => ((user as User)?.isLoggedIn ? 'signed in' : 'not signed in')
  ]
};

const propertyGroups = {
  Product: keysFrom<typeof definitions>([
    'product_id',
    'variant',
    'sku',
    'name',
    'brand',
    'price',
    'original_price',
    'image_url',
    'product_availability',
    'currency',
    'inventory_type',
    'badge'
  ]),
  Products: keysFrom<typeof definitions>(['products']),
  Cart: keysFrom<typeof definitions>(['cart_id', 'cart_type']),
  Order: keysFrom<typeof definitions>([
    'order_id',
    'order_number',
    'total',
    'currency',
    'shipping',
    'tax',
    'first_order',
    'coupon',
    'discount',
    'subtotal'
  ]),
  SecondPayment: keysFrom<typeof definitions>([
    'order_id',
    'order_number',
    'paid_remaining_balance',
    'currency',
    'coupon',
    'discount'
  ]),
  Checkout: keysFrom<typeof definitions>([
    'subtotal',
    'total',
    'shipping',
    'tax',
    'coupon',
    'currency',
    'revenue'
  ]),
  CheckoutStep: keysFrom<typeof definitions>([
    'checkout_step',
    'shipping_method',
    'payment_method'
  ]),
  SearchInfo: keysFrom<typeof definitions>(['query', 'search_type']),
  ListBrand: keysFrom<typeof definitions>(['brand']),
  CJReferral: keysFrom<typeof definitions>(['cj_aid', 'cj_pid', 'cj_sid', 'cj_event']),
  Experiment: keysFrom<typeof definitions>(['experiment_id', 'variation_id']),
  User: keysFrom<typeof definitions>(['signed_in'])
};

const events = {
  // Make sure that all events containing "Product" or "Products" happen _after_
  // the page event for the page that triggers them, or else GTM will be missing
  // information that it needs to fire tags with correct information.
  // Use the deferUntilNewTitle function with tc.track to make sure that the
  // timing is correct.
  'Product Viewed': keysFrom<typeof propertyGroups>(['Product', 'User']),
  'Product Clicked': keysFrom<typeof propertyGroups>(['Product']),
  'Product List Viewed': keysFrom<typeof propertyGroups>(['Products', 'ListBrand']),
  'Product Added': keysFrom<typeof propertyGroups>(['Product', 'Cart', 'Products']),
  'Product Removed': keysFrom<typeof propertyGroups>(['Product', 'Cart']),
  'Products Searched': keysFrom<typeof propertyGroups>(['SearchInfo']),
  'Order Converted': keysFrom<typeof propertyGroups>(['Order', 'Products', 'CJReferral']),
  'Second Payment Completed': keysFrom<typeof propertyGroups>(['SecondPayment', 'Products']),
  'Product Added to Wishlist': keysFrom<typeof propertyGroups>(['Product']),
  'Product Removed from Wishlist': keysFrom<typeof propertyGroups>(['Product']),
  'Wishlist Product Added to Cart': keysFrom<typeof propertyGroups>(['Product', 'Cart']),
  'Cart Viewed': keysFrom<typeof propertyGroups>(['Cart', 'Products']),
  'Checkout Started': keysFrom<typeof propertyGroups>(['Checkout', 'Cart', 'Products']),
  'Checkout Step Viewed': keysFrom<typeof propertyGroups>([
    'Cart',
    'Checkout',
    'Products',
    'CheckoutStep'
  ]),
  'Checkout Step Completed': keysFrom<typeof propertyGroups>([
    'Cart',
    'Checkout',
    'CheckoutStep',
    'Products'
  ]),
  'Promotion Clicked': keysFrom<typeof propertyGroups>([]),
  'Payment Info Entered': keysFrom<typeof propertyGroups>(['Cart', 'CheckoutStep']),
  'Registration Completed': keysFrom<typeof propertyGroups>([]),
  'Experiment Viewed': keysFrom<typeof propertyGroups>(['Experiment'])
};

const appendToEveryEvent = {
  srx: true,
  event_source: 'astrarium'
};

export const dictionary = new Dictionary<
  keyof typeof events,
  keyof typeof propertyGroups,
  keyof typeof definitions
>(events, propertyGroups, definitions, appendToEveryEvent);
